import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { ArrowLeft, FileText, CheckCircle, XCircle, AlertCircle, Building2, User, Calendar, Tag, Shield, Lock, Brain, FileCheck, Loader2, Download } from 'lucide-react';
import type { User as UserType } from '../App';
import { getSubmission, downloadDocument, submitReviewerAssessment, requestFilingDocumentationPrep } from '../api/innovations';
import { approveReviewerFeedback, requestReviewerFeedbackChanges } from '../api/admin';
import type { InnovationSubmission, Document } from '../api/innovations';
import { innovationReviewCriteria, ipProtectionTypes } from '../lib/reviewCriteria';
import { MOCK_AI_ASSESSMENT } from '../lib/mockAiAssessment';
import { AiAssessmentAssistPanel } from './review/AiAssessmentAssistPanel';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface ReviewInterfaceProps {
  innovationId: string;
  reviewer: UserType;
  onBack: () => void;
  /** When `ai`, shows AI suggestion panel (preview; same form as manual, fully editable). */
  assessmentMode?: 'ai' | 'manual';
}

type ViewInnovation = {
  id: string;
  title: string;
  description: string;
  submissionDate: string;
  submittedBy: string;
  institution?: string;
  category: string;
  stage: string;
  keywords: string[];
  targetUsers: string;
  status: string;
  documents?: Document[];
  decision?: 'has-potential' | 'no-potential';
  ipProtectionTypes?: string[];
  ipProtectionRecommendation?: string;
  /** HTTPS link to full written report (shared doc, PDF host, etc.) */
  comprehensiveReportUrl?: string;
  assessmentMethod?: 'ai' | 'manual';
  filingRequest?: { selectedTypes: string[]; notifiedToReviewer: boolean; notificationDate?: string };
  filingDocumentationPrep?: InnovationSubmission['filing_documentation_prep'];
  institutionUuid?: string | null;
  feedback_approval_status?: 'pending_superadmin_review' | 'approved' | 'changes_requested';
  feedback_rejection_reason?: string;
};

const FEEDBACK_STATUS_LABELS: Record<'pending_superadmin_review' | 'approved' | 'changes_requested', string> = {
  pending_superadmin_review: 'Pending Superadmin Approval',
  approved: 'Approved and Sent to Innovator',
  changes_requested: 'Changes Requested from Reviewer',
};

function submissionToView(s: InnovationSubmission): ViewInnovation {
  return {
    id: s.submission_uuid,
    title: s.title,
    description: s.abstract,
    submissionDate: s.submission_date || '',
    submittedBy: s.submitter_email || '—',
    institution: s.institution_name || undefined,
    category: s.technology_area || 'General',
    stage: s.current_state || 'idea',
    keywords: s.keywords || [],
    targetUsers: s.expected_outcome || '—',
    status: s.status,
    documents: s.documents || [],
    decision: s.review_decision,
    ipProtectionTypes: s.recommended_ip_types || [],
    ipProtectionRecommendation: s.ip_protection_recommendation,
    comprehensiveReportUrl: s.comprehensive_report_url || '',
    filingRequest: s.filing_request_notified_to_reviewer || s.filing_request?.notified_to_reviewer
      ? {
          selectedTypes: s.filing_request?.selected_types || s.filing_request_selected_types || [],
          notifiedToReviewer: true,
          notificationDate: s.filing_request?.notification_date || s.filing_request_notified_at
        }
      : undefined,
    filingDocumentationPrep: s.filing_documentation_prep,
    institutionUuid: s.institution_uuid ?? null,
    feedback_approval_status: s.feedback_approval_status,
    feedback_rejection_reason: s.feedback_rejection_reason,
  };
}

export function ReviewInterface({ innovationId, reviewer, onBack, assessmentMode = 'manual' }: ReviewInterfaceProps) {
  const [innovation, setInnovation] = useState<ViewInnovation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [decision, setDecision] = useState<'has-potential' | 'no-potential' | ''>('');
  const [selectedIPTypes, setSelectedIPTypes] = useState<string[]>([]);
  const [ipProtectionRec, setIpProtectionRec] = useState('');
  const [filingInstructions, setFilingInstructions] = useState('');
  const [comprehensiveReportUrl, setComprehensiveReportUrl] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [feedbackActionLoading, setFeedbackActionLoading] = useState(false);

  const loadSubmission = async () => {
    const sub = await getSubmission(innovationId);
    setInnovation(submissionToView(sub));
    setSubmitted(sub.status === 'completed' || sub.status === 'rejected');
    setDecision(sub.review_decision || '');
    setSelectedIPTypes(Array.isArray(sub.recommended_ip_types) ? sub.recommended_ip_types : []);
    setIpProtectionRec(sub.ip_protection_recommendation || '');
    setFilingInstructions(sub.filing_instructions || '');
    setComprehensiveReportUrl(sub.comprehensive_report_url || '');
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      if (!UUID_REGEX.test(innovationId)) {
        if (!cancelled) {
          setError('Invalid submission ID. Use a link from your dashboard.');
          setLoading(false);
        }
        return;
      }
      try {
        const sub = await getSubmission(innovationId);
        if (cancelled) return;
        setInnovation(submissionToView(sub));
        setSubmitted(sub.status === 'completed' || sub.status === 'rejected');
        if (sub.review_decision) {
          setDecision(sub.review_decision);
        }
        if (Array.isArray(sub.recommended_ip_types)) {
          setSelectedIPTypes(sub.recommended_ip_types);
        }
        if (sub.ip_protection_recommendation) {
          setIpProtectionRec(sub.ip_protection_recommendation);
        }
        if (sub.filing_instructions) {
          setFilingInstructions(sub.filing_instructions);
        }
        setComprehensiveReportUrl(sub.comprehensive_report_url || '');
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load submission');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [innovationId]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-6 flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--gold)]" />
      </div>
    );
  }

  if (error || !innovation) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error || 'Innovation not found.'}</AlertDescription>
        </Alert>
        <Button onClick={onBack} className="mt-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
      </div>
    );
  }

  const handleSubmitReview = async () => {
    if (!decision || !ipProtectionRec) {
      alert('Please complete the assessment decision and Intellectual Property protection recommendation');
      return;
    }
    if (decision === 'has-potential' && selectedIPTypes.length === 0) {
      alert('Please select at least one Intellectual Property type for innovations with protection potential');
      return;
    }
    if (!innovation) return;
    try {
      const urlTrim = comprehensiveReportUrl.trim();
      await submitReviewerAssessment(innovation.id, {
        decision,
        ip_protection_types: selectedIPTypes,
        ip_protection_recommendation: ipProtectionRec,
        filing_instructions: filingInstructions || undefined,
        comprehensive_report_url: urlTrim || null,
      });
      setSubmitted(true);
      alert('Assessment submitted and sent to superadmin for approval before innovator can view feedback.');
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : 'Failed to submit assessment';
      alert(msg);
    }
  };

  const toggleIPType = (type: string) => {
    if (selectedIPTypes.includes(type)) {
      setSelectedIPTypes(selectedIPTypes.filter(t => t !== type));
    } else {
      setSelectedIPTypes([...selectedIPTypes, type]);
    }
  };

  const applyAiSuggestionAll = () => {
    const m = MOCK_AI_ASSESSMENT;
    setDecision(m.decision);
    const valid = m.ipTypes.filter((t) => ipProtectionTypes.some((p) => p.type === t));
    setSelectedIPTypes(valid.length > 0 ? valid : []);
    setIpProtectionRec(m.recommendation);
  };

  const applyAiRecommendationOnly = () => {
    setIpProtectionRec(MOCK_AI_ASSESSMENT.recommendation);
  };

  const isReadOnly = (reviewer.role !== 'reviewer' && reviewer.role !== 'admin') || (submitted && reviewer.role !== 'admin');
  const isSuperAdmin = reviewer.role === 'super_admin';
  const feedbackStatusLabel = innovation.feedback_approval_status
    ? FEEDBACK_STATUS_LABELS[innovation.feedback_approval_status]
    : FEEDBACK_STATUS_LABELS.pending_superadmin_review;

  const handleAdminOverride = () => {
    if (!decision || !ipProtectionRec) {
      alert('Please complete the assessment decision and Intellectual Property protection recommendation before overriding');
      return;
    }
    if (decision === 'has-potential' && selectedIPTypes.length === 0) {
      alert('Please select at least one Intellectual Property type for innovations with protection potential');
      return;
    }
    alert('Admin override saved successfully!');
  };

  const handleRequestFilingDocumentation = async () => {
    if (!innovation) return;
    if (
      reviewer.role !== 'innovator' &&
      reviewer.role !== 'institution' &&
      reviewer.role !== 'institutional_admin'
    ) {
      alert('Filing documentation requests are only available for submitters.');
      return;
    }
    setNotifyLoading(true);
    try {
      await requestFilingDocumentationPrep(innovation.id);
      await loadSubmission();
      alert(
        innovation.institutionUuid
          ? 'Request recorded. A super administrator will prepare documentation under your institution’s external agreement and publish a link when it is ready.'
          : 'Request recorded. A super administrator will prepare your filing documentation and publish a link when it is ready.'
      );
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : 'Failed to submit request';
      alert(msg);
    } finally {
      setNotifyLoading(false);
    }
  };

  const handleApproveFeedback = async () => {
    if (!innovation) return;
    setFeedbackActionLoading(true);
    try {
      await approveReviewerFeedback(innovation.id);
      await loadSubmission();
      alert('Feedback approved and sent to innovator.');
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : 'Failed to approve feedback';
      alert(msg);
    } finally {
      setFeedbackActionLoading(false);
    }
  };

  const handleRequestChanges = async () => {
    if (!innovation) return;
    const reason = window.prompt('Reason for changes requested (optional):') || undefined;
    setFeedbackActionLoading(true);
    try {
      await requestReviewerFeedbackChanges(innovation.id, reason);
      await loadSubmission();
      alert('Changes requested from reviewer.');
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : 'Failed to request reviewer changes';
      alert(msg);
    } finally {
      setFeedbackActionLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
        <div className="flex items-center gap-3">
          {assessmentMode === 'ai' ? (
            <Badge variant="outline" className="text-[var(--royal)] border-[var(--royal)]">
              <Brain className="w-3 h-3 mr-1" />
              AI-assisted assessment
            </Badge>
          ) : (
            <Badge variant="outline" className="text-[var(--royal)] border-[var(--royal)]">
              <FileCheck className="w-3 h-3 mr-1" />
              Manual assessment
            </Badge>
          )}
          {submitted && innovation.status !== 'pending-assessment' && (
            <Badge className="bg-[var(--royal)] hover:opacity-90 text-white">
              <CheckCircle className="w-4 h-4 mr-2" />
              Assessment Completed
            </Badge>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Document Preview */}
        <div className="lg:col-span-1 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Supporting Documents
              </CardTitle>
              <CardDescription>
                Files uploaded with this submission. Reviewers and administrators can download.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {innovation.documents && innovation.documents.length > 0 ? (
                <ul className="space-y-2">
                  {innovation.documents.map((doc) => (
                    <li
                      key={doc.doc_uuid}
                      className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4 text-slate-500 flex-shrink-0" />
                        <span className="text-sm text-slate-900 truncate" title={doc.file_name}>
                          {doc.file_name}
                        </span>
                        <span className="text-xs text-slate-500 flex-shrink-0">
                          ({(doc.file_size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      {(reviewer.role === 'reviewer' || reviewer.role === 'admin' || reviewer.role === 'super_admin') && (
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={downloadingDocId === doc.doc_uuid}
                          onClick={async () => {
                            setDownloadingDocId(doc.doc_uuid);
                            try {
                              await downloadDocument(innovation.id, doc.doc_uuid, doc.file_name);
                            } catch (e: unknown) {
                              console.error('Download failed', e);
                              const msg = e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : 'Download failed';
                              alert(msg);
                            } finally {
                              setDownloadingDocId(null);
                            }
                          }}
                        >
                          {downloadingDocId === doc.doc_uuid ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4 mr-1" />
                          )}
                          Download
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="rounded-lg border-2 border-dashed border-slate-200 bg-slate-50/50 flex items-center justify-center py-8 text-slate-500 text-sm">
                  No supporting documents uploaded
                </div>
              )}
              {innovation.documents && innovation.documents.length > 0 && (reviewer.role === 'reviewer' || reviewer.role === 'admin' || reviewer.role === 'super_admin') && (
                <Alert className="mt-4 bg-slate-50 border-slate-200">
                  <Lock className="h-4 w-4 text-slate-600" />
                  <AlertDescription className="text-slate-700 text-xs">
                    Documents are stored encrypted. Download is available only to reviewers and administrators.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Innovation Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center gap-2 text-slate-500 mb-1">
                  <Tag className="w-4 h-4" />
                  <span>ID</span>
                </div>
                <div className="text-slate-900">{innovation.id}</div>
              </div>

              <Separator />

              <div>
                <div className="flex items-center gap-2 text-slate-500 mb-1">
                  <User className="w-4 h-4" />
                  <span>Submitted By</span>
                </div>
                <div className="text-slate-900">{innovation.submittedBy}</div>
                {innovation.institution && (
                  <div className="flex items-center gap-2 text-slate-600 mt-1">
                    <Building2 className="w-4 h-4" />
                    {innovation.institution}
                  </div>
                )}
              </div>

              <Separator />

              <div>
                <div className="flex items-center gap-2 text-slate-500 mb-1">
                  <Calendar className="w-4 h-4" />
                  <span>Submission Date</span>
                </div>
                <div className="text-slate-900">
                  {new Date(innovation.submissionDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
              </div>

              <Separator />

              <div>
                <div className="text-slate-500 mb-2">Category & Stage</div>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">{innovation.category}</Badge>
                  <Badge variant="outline" className="capitalize">
                    {innovation.stage.replace('-', ' ')}
                  </Badge>
                </div>
              </div>

              <Separator />

              <div>
                <div className="text-slate-500 mb-2">Keywords</div>
                <div className="flex flex-wrap gap-1">
                  {innovation.keywords.map(keyword => (
                    <Badge key={keyword} variant="secondary" className="text-xs">
                      {keyword}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Filing Request Notification */}
          {innovation.filingRequest?.notifiedToReviewer && reviewer.role === 'reviewer' && (
            <Card className="border-[rgba(26,86,219,0.3)] bg-[var(--gold-pale)]">
              <CardHeader>
                <CardTitle className="text-[var(--navy)] font-display flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Filing Request
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-blue-800 mb-3">
                  The innovator has requested guidance for filing the following Intellectual Property types:
                </p>
                <div className="flex flex-wrap gap-2">
                  {innovation.filingRequest.selectedTypes.map(type => (
                    <Badge key={type} className="bg-[var(--gold)] text-[var(--navy)]">{type}</Badge>
                  ))}
                </div>
                <p className="text-blue-700 text-sm mt-3">
                  Notification Date: {innovation.filingRequest.notificationDate}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Review Interface */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Innovation Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-slate-900">Description</Label>
                <p className="text-slate-600 mt-2">{innovation.description}</p>
              </div>
              <Separator />
              <div>
                <Label className="text-slate-900">Target Users / Impact Area</Label>
                <p className="text-slate-600 mt-2">{innovation.targetUsers}</p>
              </div>
            </CardContent>
          </Card>

          {assessmentMode === 'ai' && (
            <AiAssessmentAssistPanel
              suggestion={MOCK_AI_ASSESSMENT}
              onApplyAll={applyAiSuggestionAll}
              onApplyRecommendationOnly={applyAiRecommendationOnly}
              disabled={isReadOnly}
            />
          )}

          <Tabs defaultValue="assessment" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="assessment">Assessment</TabsTrigger>
              <TabsTrigger value="criteria">Review Criteria</TabsTrigger>
            </TabsList>

            <TabsContent value="assessment" className="space-y-6">
              {innovation.feedback_approval_status === 'pending_superadmin_review' && (
                <Alert className="bg-[var(--gold-pale)] border-[rgba(201,168,76,0.3)]">
                  <AlertDescription className="text-[var(--navy)]">
                    <strong>{feedbackStatusLabel}:</strong> Reviewer feedback is waiting for superadmin approval before it is released to the innovator.
                  </AlertDescription>
                </Alert>
              )}
              {isSuperAdmin && innovation.feedback_approval_status === 'pending_superadmin_review' && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={handleApproveFeedback}
                    disabled={feedbackActionLoading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {feedbackActionLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    Approve and Send to Innovator
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleRequestChanges}
                    disabled={feedbackActionLoading}
                  >
                    Request Changes from Reviewer
                  </Button>
                </div>
              )}
              {innovation.feedback_approval_status === 'changes_requested' && (
                <Alert className="bg-amber-50 border-amber-200">
                  <AlertDescription className="text-amber-900">
                    <strong>{feedbackStatusLabel}:</strong> Superadmin requested updates to this feedback. Update the assessment and submit again.
                    {innovation.feedback_rejection_reason ? ` Reason: ${innovation.feedback_rejection_reason}` : ''}
                  </AlertDescription>
                </Alert>
              )}
              {reviewer.role === 'admin' && submitted && (
                <Alert className="bg-amber-50 border-amber-200">
                  <Shield className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-900">
                    <strong>Admin Override Mode:</strong> You have full authority to modify this assessment. You can change the decision, Intellectual Property types, and recommendations regardless of the original reviewer's assessment.
                  </AlertDescription>
                </Alert>
              )}
              
              <Card>
                <CardHeader>
                  <CardTitle>{isReadOnly ? 'Assessment Result' : 'Assessment Decision'}</CardTitle>
                  <CardDescription>
                    {isReadOnly
                      ? 'Independent Reviewer\'s evaluation on Intellectual Property protection potential'
                      : assessmentMode === 'ai'
                        ? 'You may apply AI suggestions above, then override any choice below. Your final entries are what get submitted.'
                        : 'Determine if this innovation has potential for Intellectual Property protection'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                      onClick={() => !isReadOnly && setDecision('has-potential')}
                      disabled={isReadOnly}
                      className={`p-6 border-2 rounded-lg text-left transition-all ${
                        decision === 'has-potential'
                          ? 'border-[var(--royal)] bg-[rgba(26,86,219,0.08)]'
                          : 'border-[rgba(201,168,76,0.2)] hover:border-[var(--royal)]'
                      } ${isReadOnly ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <CheckCircle className={`w-6 h-6 ${decision === 'has-potential' ? 'text-[var(--royal)]' : 'text-slate-400'}`} />
                        <h3 className="text-slate-900">Has Potential</h3>
                      </div>
                      <p className="text-slate-600 text-sm">
                        Innovation has potential for Intellectual Property protection
                      </p>
                    </button>

                    <button
                      onClick={() => !isReadOnly && setDecision('no-potential')}
                      disabled={isReadOnly}
                      className={`p-6 border-2 rounded-lg text-left transition-all ${
                        decision === 'no-potential'
                          ? 'border-red-500 bg-red-50'
                          : 'border-slate-200 hover:border-red-300'
                      } ${isReadOnly ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <XCircle className={`w-6 h-6 ${decision === 'no-potential' ? 'text-red-600' : 'text-slate-400'}`} />
                        <h3 className="text-slate-900">No Potential</h3>
                      </div>
                      <p className="text-slate-600 text-sm">
                        Innovation has no potential for Intellectual Property protection
                      </p>
                    </button>
                  </div>
                </CardContent>
              </Card>

              {decision === 'has-potential' && (
                <Card>
                  <CardHeader>
                    <CardTitle>Intellectual Property Protection Types</CardTitle>
                    <CardDescription>
                      {isReadOnly
                        ? 'Recommended Intellectual Property types for this innovation'
                        : 'Select which Intellectual Property types apply to this innovation'
                      }
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {ipProtectionTypes.map((ipType) => (
                      <div
                        key={ipType.type}
                        className={`p-4 border-2 rounded-lg transition-all ${
                          selectedIPTypes.includes(ipType.type)
                            ? 'border-[var(--gold)] bg-[var(--gold-pale)]'
                            : 'border-slate-200'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={selectedIPTypes.includes(ipType.type)}
                            onCheckedChange={() => !isReadOnly && toggleIPType(ipType.type)}
                            disabled={isReadOnly}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="text-slate-900">{ipType.type}</h4>
                              {selectedIPTypes.includes(ipType.type) && (
                                <Badge variant="secondary" className="text-xs">Selected</Badge>
                              )}
                            </div>
                            <p className="text-slate-600 text-sm mb-2">{ipType.description}</p>
                            <div className="text-slate-500 text-xs space-y-1">
                              <p><strong>Suitable for:</strong> {ipType.suitable}</p>
                              <p><strong>Duration:</strong> {ipType.duration}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader>
                  <CardTitle>Intellectual Property Protection Recommendation</CardTitle>
                  <CardDescription>
                    {isReadOnly
                      ? 'Expert guidance on Intellectual Property protection strategy for this innovation'
                      : 'Provide detailed guidance on Intellectual Property protection strategy'
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Textarea
                    rows={6}
                    placeholder={isReadOnly
                      ? "Recommendations on Intellectual Property protection will be provided after review..."
                      : "Provide comprehensive Intellectual Property protection recommendations, including specific types of protection, filing strategies, and key considerations..."
                    }
                    value={ipProtectionRec}
                    onChange={(e) => setIpProtectionRec(e.target.value)}
                    disabled={isReadOnly}
                    className="mb-2"
                  />
                  <p className="text-slate-500 text-sm">
                    This field must always use the full words "Intellectual Property" (not abbreviations)
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Comprehensive report link</CardTitle>
                  <CardDescription>
                    {isReadOnly
                      ? 'External link to the full reviewer report (stays clickable after submission for administrators and stakeholders).'
                      : 'Optional: paste an https link to your full written report (e.g. Google Docs, OneDrive, or institutional repository). This remains an active link after you submit, unlike the text fields above.'}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {!isReadOnly ? (
                    <>
                      <Label htmlFor="comprehensive-report-url">Report URL (https://…)</Label>
                      <Input
                        id="comprehensive-report-url"
                        type="url"
                        inputMode="url"
                        autoComplete="off"
                        placeholder="https://…"
                        value={comprehensiveReportUrl}
                        onChange={(e) => setComprehensiveReportUrl(e.target.value)}
                        className="font-mono text-sm"
                      />
                      <p className="text-slate-500 text-sm">
                        Only http and https links are accepted. Leave blank if the recommendation above is sufficient.
                      </p>
                    </>
                  ) : comprehensiveReportUrl.trim() ? (
                    <div>
                      <Label className="text-slate-700">Full report</Label>
                      <a
                        href={comprehensiveReportUrl.trim()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex text-[var(--royal)] underline break-all hover:opacity-90"
                      >
                        {comprehensiveReportUrl.trim()}
                      </a>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm">No external report link was provided.</p>
                  )}
                </CardContent>
              </Card>

              {decision === 'has-potential' && (
                <Card>
                  <CardHeader>
                    <CardTitle>
                      {reviewer.role === 'reviewer'
                        ? 'Filing Instructions'
                        : reviewer.role === 'innovator' ||
                            reviewer.role === 'institution' ||
                            reviewer.role === 'institutional_admin'
                          ? 'Filing documentation preparation'
                          : 'Filing documentation'}
                    </CardTitle>
                    <CardDescription>
                      {reviewer.role === 'reviewer'
                        ? 'Provide detailed instructions on how to file for the recommended Intellectual Property protection types'
                        : reviewer.role === 'innovator' ||
                            reviewer.role === 'institution' ||
                            reviewer.role === 'institutional_admin'
                          ? 'Request prepared filing documentation after assessment (institutions: terms outside the app; individuals: billing recorded in-app first)'
                          : 'Available after superadmin has released feedback to the submitter'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {reviewer.role === 'reviewer' ? (
                      <>
                        <Alert className="bg-[var(--gold-pale)] border-[rgba(201,168,76,0.3)]">
                          <Shield className="h-4 w-4 text-[var(--gold)]" />
                          <AlertDescription className="text-[var(--navy)]">
                            <strong>Important:</strong> These instructions will be sent to the innovator and will trigger a notification to you when they are ready to file for Intellectual Property protection.
                          </AlertDescription>
                        </Alert>
                        <Textarea
                          rows={6}
                          placeholder="Provide step-by-step instructions on how to file for the selected Intellectual Property types. Include required documents, filing procedures, estimated timelines, and any specific recommendations..."
                          value={filingInstructions}
                          onChange={(e) => setFilingInstructions(e.target.value)}
                          disabled={isReadOnly}
                        />
                      </>
                    ) : reviewer.role === 'innovator' ||
                      reviewer.role === 'institution' ||
                      reviewer.role === 'institutional_admin' ? (
                      <>
                        <Alert className="bg-[var(--gold-pale)] border-[rgba(201,168,76,0.3)]">
                          <Shield className="h-4 w-4 text-[var(--gold)]" />
                          <AlertDescription className="text-[var(--navy)] space-y-2">
                            <p>
                              Insolip prepares <strong>filing documentation</strong> for you to use with your chosen
                              Intellectual Property office or agent; Insolip does not file on your behalf. Only a{' '}
                              <strong>super administrator</strong> prepares and publishes the documentation package link.
                            </p>
                            {innovation.institutionUuid || reviewer.role === 'institutional_admin' ? (
                              <p>
                                For <strong>institutions</strong>, commercial terms are agreed <strong>outside this
                                application</strong>. Submit the request here after assessment so Insolip can align
                                preparation with your external agreement.
                              </p>
                            ) : (
                              <p>
                                For <strong>individual innovators</strong>, Insolip records <strong>billing</strong> in
                                this application before you can submit the request; payment is not processed in the app.
                              </p>
                            )}
                          </AlertDescription>
                        </Alert>
                        {innovation.filingDocumentationPrep?.package_url && (
                          <div className="rounded-lg border border-emerald-200 bg-emerald-50/80 p-4 text-sm text-emerald-950">
                            <strong>Documentation package:</strong>{' '}
                            <a
                              href={innovation.filingDocumentationPrep.package_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[var(--royal)] underline break-all"
                            >
                              {innovation.filingDocumentationPrep.package_url}
                            </a>
                          </div>
                        )}
                        {innovation.filingDocumentationPrep?.requested_at ? (
                          <p className="text-slate-600 text-sm">
                            You have already requested filing documentation preparation. Track status on your dashboard.
                          </p>
                        ) : (
                          <>
                            {!innovation.institutionUuid &&
                              reviewer.role === 'innovator' &&
                              !innovation.filingDocumentationPrep?.billing_cleared_at &&
                              !innovation.filingDocumentationPrep?.payment_confirmed_at && (
                                <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md p-3">
                                  Insolip must record billing for this submission before you can request preparation.
                                </p>
                              )}
                            <Button
                              className="w-full"
                              onClick={handleRequestFilingDocumentation}
                              disabled={
                                notifyLoading ||
                                (!innovation.institutionUuid &&
                                  reviewer.role === 'innovator' &&
                                  !innovation.filingDocumentationPrep?.billing_cleared_at &&
                                  !innovation.filingDocumentationPrep?.payment_confirmed_at)
                              }
                            >
                              <Shield className="w-4 h-4 mr-2" />
                              {notifyLoading ? 'Submitting…' : 'Request filing documentation preparation'}
                            </Button>
                          </>
                        )}
                      </>
                    ) : (
                      <Alert className="bg-slate-50 border-slate-200">
                        <AlertDescription className="text-slate-700">
                          Filing request notification is available on the innovator view after superadmin approval.
                        </AlertDescription>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              )}

              {!isReadOnly && (
                <Button
                  onClick={handleSubmitReview}
                  className="w-full"
                  size="lg"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Submit Assessment
                </Button>
              )}

              {reviewer.role === 'admin' && (
                <Button
                  onClick={handleAdminOverride}
                  className="w-full bg-red-600 hover:bg-red-700"
                  size="lg"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Admin Override
                </Button>
              )}
            </TabsContent>

            <TabsContent value="criteria" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Innovation Assessment Criteria</CardTitle>
                  <CardDescription>
                    {isReadOnly
                      ? 'Your innovation will be assessed based on this comprehensive evaluation framework focused on Intellectual Property protection potential'
                      : 'Comprehensive evaluation framework focused on Intellectual Property protection potential'
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="w-full">
                    {innovationReviewCriteria.map((criteria) => (
                      <AccordionItem key={criteria.id} value={criteria.id}>
                        <AccordionTrigger>
                          <div className="flex items-center justify-between w-full pr-4">
                            <span className="text-slate-900">{criteria.title}</span>
                            <Badge variant="outline">{criteria.weightage}% Weight</Badge>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="space-y-3 pt-2">
                            <p className="text-slate-600">{criteria.description}</p>
                            <div>
                              <Label className="text-slate-700 mb-2 block">
                                {isReadOnly ? 'Evaluation Factors:' : 'Assessment Points:'}
                              </Label>
                              <ul className="space-y-2">
                                {criteria.assessmentPoints.map((point, idx) => (
                                  <li key={idx} className="flex items-start gap-2 text-slate-600">
                                    <span className="text-[var(--gold)] mt-1">•</span>
                                    <span className="flex-1">{point}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}